import Script from 'next/script';

import { Col, Picture, Row, Button, Banner, Typography } from 'darrius';

import { Header } from '../../components/atoms';
import { HeadContent } from '../../components';

import { type IDocumentsValidationProps } from './@interfaces';

import styles from './DocumentsValidation.module.scss';
import { useState } from 'react';
import { validateDocumentService } from '@/ui/painel/_repositories/fetch-background-check';
import FeedbackValidationInProgressDocument from './partials/DocumentFlow/FeedbackValidationInProgressDocument';
import FeedbackValidationInProgressLiveness from './partials/LivenessFlow/FeedbackValidationInProgressLiveness';
import { DocumentValidationResponseGet } from '@/types/idwall';
import FeedbackValidationSuccessLiveness from './partials/LivenessFlow/FeedbackValidationSuccessLiveness';
import FeedbackValidationSuccessDocument from './partials/DocumentFlow/FeedbackValidationSuccessDocument';
import FeedbackValidationErrorLiveness from './partials/LivenessFlow/FeedbackValidationErrorLiveness';
import FeedbackValidationErrorDocument from './partials/DocumentFlow/FeedbackValidationErrorDocument';
import FeedbackValidationRejectedLiveness from './partials/LivenessFlow/FeedbackValidationRejectedLiveness';
import FeedbackValidationRejectedDocument from './partials/DocumentFlow/FeedbackValidationRejectedDocument';
import FeedbackValidationTimeoutLiveness from './partials/LivenessFlow/FeedbackValidationTimeoutLiveness';
import FeedbackValidationTimeoutDocument from './partials/DocumentFlow/FeedbackValidationTimeoutDocument';

const CHAT_URL =
  'https://www11.directtalk.com.br/static/beta/admin/partials/misc/dtbot_preview.html?token=16c08d5a-eaf0-42cf-8c13-d4bcb0e39e08&fakebot=true';

const getPageHeading = (shouldValidateDocuments) => {
  if (shouldValidateDocuments) {
    return 'Para continuar usando o GetNinjas, você precisa validar seus documentos. Isso garante a segurança dos clientes e dos profissionais. Habilite as permissões de câmera para seguir o processo.';
  }

  return 'Aguarde um pouquinho, nossa equipe está analisando sua imagem';
};

export default function DocumentsValidation({
  validation,
}: IDocumentsValidationProps) {
  const shouldValidateDocuments = validation != null;

  const sectionText = getPageHeading(shouldValidateDocuments);

  const shouldUseIdWall =
    process.env.NEXT_PUBLIC_USE_IDWALL_VALIDATION === 'true';

  const [validationState, setValidationState] = useState({
    showValidateDocumentsBanner: true,
    showIDWallValidation: true,
    showFeedbackValidationInProgress: false,
    showFeedbackValidationVerified: false,
    showFeedbackValidationRejected: false,
    showFeedbackValidationError: false,
    showFeedbackValidationTimeout: false,
    currentFlow: '',
  });

  const updateValidationState = (updates) => {
    setValidationState((prevState) => ({ ...prevState, ...updates }));
  };

  const listenerClickIDWallValidation = async () => {
    const response = await getValidationStatus();

    handleValidationStatus(response, undefined);
  };

  const getValidationStatus = async () => {
    const response = await validateDocumentService({
      method: 'GET',
      hash: validation!.hash,
    });

    return response;
  };

  const handleValidationStatus = (
    response,
    interval?: NodeJS.Timeout | undefined
  ) => {
    updateValidationState({ currentFlow: response.data.current_flow });

    switch (response?.data?.status) {
      case 'not_sent':
        startValidationIDWall(response);
        break;
      case 'started':
        const isPoolingStarted = !!interval;
        updateValidationState({
          showIDWallValidation: false,
          showValidateDocumentsBanner: false,
          showFeedbackValidationInProgress: isPoolingStarted,
          showFeedbackValidationVerified: false,
          showFeedbackValidationRejected: false,
          showFeedbackValidationError: false,
          showFeedbackValidationTimeout: !isPoolingStarted,
        });
        break;
      case 'verified':
        updateValidationState({
          showIDWallValidation: false,
          showValidateDocumentsBanner: false,
          showFeedbackValidationInProgress: false,
          showFeedbackValidationVerified: true,
          showFeedbackValidationRejected: false,
          showFeedbackValidationError: false,
        });
        if (interval) {
          clearInterval(interval);
        }
        break;
      case 'rejected':
        if (interval) {
          clearInterval(interval);
        }
        updateValidationState({
          showIDWallValidation: false,
          showValidateDocumentsBanner: false,
          showFeedbackValidationInProgress: false,
          showFeedbackValidationVerified: false,
          showFeedbackValidationRejected: true,
          showFeedbackValidationError: false,
        });

        break;
      default:
        if (
          response?.status === 400 ||
          response?.status === 500 ||
          response?.data?.message === 'Unexpected error'
        ) {
          handleValidationStatusError(interval);
        }
    }
  };

  const handleValidationStatusError = (
    interval?: NodeJS.Timeout | undefined
  ) => {
    updateValidationState({
      showIDWallValidation: false,
      showValidateDocumentsBanner: false,
      showFeedbackValidationInProgress: false,
      showFeedbackValidationVerified: false,
      showFeedbackValidationRejected: false,
      showFeedbackValidationError: true,
    });
    if (interval) {
      clearInterval(interval);
    }
  };

  const startValidationIDWall = async (responseStatus) => {
    const IDWALL_TOKENS = {
      liveness: process.env.NEXT_PUBLIC_TOKEN_SDK_IDWALL_LIVENESS,
      document: process.env.NEXT_PUBLIC_TOKEN_SDK_IDWALL_DOCUMENT,
      complete: process.env.NEXT_PUBLIC_TOKEN_SDK_IDWALL,
    };

    const IDWALL_TOKEN = IDWALL_TOKENS[responseStatus.data.current_flow] || '';

    if (window.idwSDKWeb && IDWALL_TOKEN) {
      updateValidationState({
        showValidateDocumentsBanner: false,
      });

      window.idwSDKWeb({
        token: IDWALL_TOKEN,
        onRender: () => {},
        onComplete: ({ token }) =>
          onCompleteIDWallValidation(token, responseStatus.data),
        onError: () => {},
      });
    }
  };

  const onCompleteIDWallValidation = async (
    token: string,
    responseStatus: DocumentValidationResponseGet
  ) => {
    updateValidationState({
      showIDWallValidation: false,
      showValidateDocumentsBanner: false,
      showFeedbackValidationInProgress: true,
      showFeedbackValidationVerified: false,
      showFeedbackValidationRejected: false,
      showFeedbackValidationError: false,
    });

    const responsePost = await validateDocumentService({
      sdkToken: token,
      method: 'POST',
      hash: validation!.hash,
      flowIdentifier: responseStatus.current_flow,
    });

    poolingCheckValidationStatus(responsePost);
  };

  const poolingCheckValidationStatus = async (responsePost) => {
    const { initial_delay_seconds, retry_interval_seconds, max_retries } =
      responsePost.data.polling_configuration;

    setTimeout(() => {
      let retries = 0;

      const interval = setInterval(async () => {
        const response = await getValidationStatus();
        handleValidationStatus(response, interval);
        retries++;

        if (retries >= max_retries) {
          const isPoolingFinishedAndStatusStillStarted =
            response.data.status === 'started';
          if (isPoolingFinishedAndStatusStillStarted) {
            updateValidationState({
              showIDWallValidation: false,
              showValidateDocumentsBanner: false,
              showFeedbackValidationInProgress: false,
              showFeedbackValidationVerified: false,
              showFeedbackValidationRejected: false,
              showFeedbackValidationError: false,
              showFeedbackValidationTimeout: true,
            });
          }

          clearInterval(interval);
          return;
        }
      }, retry_interval_seconds * 1000);
    }, initial_delay_seconds * 1000);
  };

  return (
    <>
      <HeadContent
        metaTitle="GetNinjas | Orçamento de Profissionais Confiáveis no GetNinjas.com.br"
        slug="validacao-de-documentos"
        metaDescription="Encontre profissionais confiáveis para pintar, reformar, traduzir um texto, te ensinar matemática, ou também passear com seu cachorro no GetNinjas"
        type="document-validation:show"
        noindex
      />

      <Header.Simple />

      <main
        className={`register-success space-top space-bottom ${styles['register-success']}`}
      >
        {shouldUseIdWall ? (
          <>
            <Script
              id="idwall-sdk"
              strategy="afterInteractive"
              src="https://sdkweb-lib.idwall.co/index.js"
            />
          </>
        ) : (
          <Script
            id="doc-check"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.ex_partner = {
                  ex_doccheck_identity_key_id: "${process.env.NEXT_PUBLIC_BACKGROUND_CHECK_KEY}",
                };
                (function (w, d, src) {
                  var h = d.getElementsByTagName("head")[0];
                  var s = d.createElement("script"); s.src = src;
                  if (!w.exDocCheck) h.appendChild(s);
                  else w.exDocCheck.init();
                })(window, document, "https://doccheck.exato.digital/doccheck.js");
              `,
            }}
          />
        )}

        {/** Elemento que recebe o iFrame da IDWall */}
        {validationState.showIDWallValidation ? (
          <div data-idw-sdk-web style={{ margin: 'auto' }}></div>
        ) : undefined}

        {validationState.showValidateDocumentsBanner ? (
          <Banner>
            <Row className="center-sm">
              <Col className="col-sm-5 col-md-2">
                <Picture
                  className={styles['icon-big']}
                  src={{
                    sm: `${process.env.NEXT_PUBLIC_ASSETS_PREFIX}/images/documents-validation.svg`,
                    md: `${process.env.NEXT_PUBLIC_ASSETS_PREFIX}/images/documents-validation.svg`,
                    lg: `${process.env.NEXT_PUBLIC_ASSETS_PREFIX}/images/documents-validation.svg`,
                  }}
                />
              </Col>
            </Row>

            <Row className="center-sm">
              <Col className="col-md-6">
                <Typography.Title size="3xl">
                  Valide seus documentos
                </Typography.Title>
                <Typography.Text className={styles.text}>
                  {sectionText}
                </Typography.Text>
              </Col>
            </Row>

            {shouldValidateDocuments && (
              <Row className="center-sm space-between">
                <Col className="col-sm-12 col-md-4">
                  <Button.Root
                    variation="primary"
                    size="small"
                    fullWidth
                    id="exDocCheckAction"
                    data-ex-doccheck-document={validation.doc}
                    data-ex-doccheck-document-hmac-hash={validation.hash}
                    onClick={() =>
                      shouldUseIdWall && listenerClickIDWallValidation()
                    }
                  >
                    Validar meus documentos
                  </Button.Root>
                  <Button.Link
                    variation="secondary"
                    size="small"
                    fullWidth
                    href={CHAT_URL}
                  >
                    Preciso de ajuda
                  </Button.Link>
                </Col>
              </Row>
            )}
          </Banner>
        ) : undefined}

        {shouldUseIdWall && validationState.showFeedbackValidationVerified ? (
          validationState.currentFlow === 'liveness' ? (
            <FeedbackValidationSuccessLiveness />
          ) : validationState.currentFlow === 'document' ||
            validationState.currentFlow === 'complete' ? (
            <FeedbackValidationSuccessDocument />
          ) : undefined
        ) : undefined}

        {shouldUseIdWall && validationState.showFeedbackValidationRejected ? (
          validationState.currentFlow === 'liveness' ? (
            <FeedbackValidationRejectedLiveness />
          ) : validationState.currentFlow === 'document' ||
            validationState.currentFlow === 'complete' ? (
            <FeedbackValidationRejectedDocument />
          ) : undefined
        ) : undefined}

        {shouldUseIdWall && validationState.showFeedbackValidationInProgress ? (
          validationState.currentFlow === 'liveness' ? (
            <FeedbackValidationInProgressLiveness />
          ) : validationState.currentFlow === 'document' ||
            validationState.currentFlow === 'complete' ? (
            <FeedbackValidationInProgressDocument />
          ) : undefined
        ) : undefined}

        {shouldUseIdWall && validationState.showFeedbackValidationTimeout ? (
          validationState.currentFlow === 'liveness' ? (
            <FeedbackValidationTimeoutLiveness />
          ) : validationState.currentFlow === 'document' ||
            validationState.currentFlow === 'complete' ? (
            <FeedbackValidationTimeoutDocument />
          ) : undefined
        ) : undefined}

        {shouldUseIdWall && validationState.showFeedbackValidationError ? (
          validationState.currentFlow === 'liveness' ? (
            <FeedbackValidationErrorLiveness />
          ) : validationState.currentFlow === 'document' ||
            validationState.currentFlow === 'complete' ? (
            <FeedbackValidationErrorDocument />
          ) : undefined
        ) : undefined}
      </main>
    </>
  );
}
